import React, { useState, useCallback } from 'react';
import { loyaltyService } from '../../services';
import type { CustomerLookup } from '../../services/loyalty.service';
import { toast } from '../../components/Toast';
import styles from './Loyalty.module.css';

export const Loyalty: React.FC = () => {
  const [code, setCode] = useState('');
  const [customer, setCustomer] = useState<CustomerLookup | null>(null);
  const [loading, setLoading] = useState(false);
  const [points, setPoints] = useState('');
  const [description, setDescription] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleLookup = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setCustomer(null);
    try {
      const result = await loyaltyService.lookupCustomer(code.trim());
      setCustomer(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Cliente non trovato';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [code]);

  const handleAssign = useCallback(async () => {
    if (!customer || !points) return;
    setProcessing(true);
    try {
      await loyaltyService.assignPoints(customer.loyaltyCode, parseInt(points), description || undefined);
      toast.success(`${points} punti assegnati a ${customer.fullName}`);
      // Refresh customer data
      const updated = await loyaltyService.lookupCustomer(customer.loyaltyCode);
      setCustomer(updated);
      setPoints('');
      setDescription('');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Errore nell\'assegnazione punti');
    } finally {
      setProcessing(false);
    }
  }, [customer, points, description]);

  const handleDeduct = useCallback(async () => {
    if (!customer || !points) return;
    if (!window.confirm(`Scalare ${points} punti a ${customer.fullName}?`)) return;
    setProcessing(true);
    try {
      await loyaltyService.deductPoints(customer.loyaltyCode, parseInt(points), description || undefined);
      toast.success(`${points} punti scalati da ${customer.fullName}`);
      const updated = await loyaltyService.lookupCustomer(customer.loyaltyCode);
      setCustomer(updated);
      setPoints('');
      setDescription('');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Errore nello scalamento punti');
    } finally {
      setProcessing(false);
    }
  }, [customer, points, description]);

  const handleReset = useCallback(() => {
    setCustomer(null);
    setCode('');
    setPoints('');
    setDescription('');
  }, []);

  return (
    <div className={styles.loyalty}>
      <div className={styles.header}>
        <h1>Gestione Punti</h1>
      </div>

      {/* Search */}
      {!customer && (
        <form onSubmit={handleLookup} className={styles.searchSection}>
          <p className={styles.searchHint}>
            Inserisci il codice fedelta' del cliente o scansiona il QR code
          </p>
          <div className={styles.searchRow}>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Codice cliente (es. A1B2C3D4)"
              className={styles.searchInput}
              autoFocus
            />
            <button type="submit" className={styles.searchBtn} disabled={loading || !code.trim()}>
              {loading ? '...' : 'Cerca'}
            </button>
          </div>
        </form>
      )}

      {/* Customer Found */}
      {customer && (
        <div className={styles.customerSection}>
          <div className={styles.customerCard}>
            <div className={styles.customerInfo}>
              <h2>{customer.fullName}</h2>
              <p className={styles.customerEmail}>{customer.email}</p>
              <p className={styles.customerCode}>Codice: {customer.loyaltyCode}</p>
            </div>
            <div className={styles.customerBalance}>
              <span className={styles.balanceLabel}>Saldo</span>
              <span className={styles.balanceValue}>{customer.pointsBalance}</span>
              <span className={styles.balanceLabel}>punti</span>
            </div>
          </div>

          <div className={styles.pointsForm}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Punti</label>
                <input
                  type="number"
                  value={points}
                  onChange={(e) => setPoints(e.target.value)}
                  min="1"
                  placeholder="Quantita'"
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup} style={{ flex: 2 }}>
                <label>Motivazione (opzionale)</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Es. Acquisto caffe'"
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.pointsActions}>
              <button
                className={styles.assignBtn}
                onClick={handleAssign}
                disabled={processing || !points || parseInt(points) <= 0}
              >
                + Assegna Punti
              </button>
              <button
                className={styles.deductBtn}
                onClick={handleDeduct}
                disabled={processing || !points || parseInt(points) <= 0}
              >
                - Scala Punti
              </button>
            </div>
          </div>

          <button className={styles.resetBtn} onClick={handleReset}>
            Cerca altro cliente
          </button>
        </div>
      )}
    </div>
  );
};

Loyalty.displayName = 'Loyalty';
